/**
 * Keystone tracker view model (pure, no DOM): timer, chest thresholds, forces, boss checklist,
 * deaths. `fillTrackerView` writes into a reusable object so the per-frame path allocates nothing
 * once the boss list has its length; the widget (tracker.ts) diffs it into the DOM.
 */
import type { ContentRegistry, DeepReadonly, World } from '@keystone/sim';

/** Chest thresholds as fractions of the timer (retail: +3 at 60%, +2 at 80%). */
export const CHEST_THRESHOLDS = { plus3: 0.6, plus2: 0.8 } as const;

export interface TrackerBoss {
  id: string;
  name: string;
  killed: boolean;
  /** The encounter is in progress. */
  engaged: boolean;
}

export type TrackerPhase = 'pre' | 'running' | 'completed' | 'depleted';

export interface TrackerView {
  dungeon: string;
  level: number;
  affixes: readonly string[];
  phase: TrackerPhase;
  timerMs: number;
  elapsedMs: number;
  /** Time left on the timer (negative when over). */
  remainingMs: number;
  over: boolean;
  /** Elapsed fraction of the timer, 0..1 (clamped). */
  timeFrac: number;
  /** Time left to beat +3 / +2 (<= 0: missed). */
  plus3LeftMs: number;
  plus2LeftMs: number;
  /** Chest the run is on pace for right now (3/2/1, 0 = over time). Final chest once completed. */
  chest: 0 | 1 | 2 | 3;
  forces: number;
  forcesRequired: number;
  /** 0..1, clamped. */
  forcesFrac: number;
  /** Percentage with one decimal as tenths (624 = 62.4%), capped at 1000. Integer for table lookups. */
  forcesTenths: number;
  forcesDone: boolean;
  bosses: TrackerBoss[];
  bossesKilled: number;
  deaths: number;
  penaltyMs: number;
  wipes: number;
  inCombat: boolean;
}

export function emptyTrackerView(): TrackerView {
  return {
    dungeon: '',
    level: 0,
    affixes: [],
    phase: 'pre',
    timerMs: 1,
    elapsedMs: 0,
    remainingMs: 0,
    over: false,
    timeFrac: 0,
    plus3LeftMs: 0,
    plus2LeftMs: 0,
    chest: 3,
    forces: 0,
    forcesRequired: 1,
    forcesFrac: 0,
    forcesTenths: 0,
    forcesDone: false,
    bosses: [],
    bossesKilled: 0,
    deaths: 0,
    penaltyMs: 0,
    wipes: 0,
    inCombat: false,
  };
}

/** Chest for a run that took `elapsed` of `timer` (0 = over time). */
export function chestFor(elapsed: number, timer: number): 0 | 1 | 2 | 3 {
  if (elapsed <= timer * CHEST_THRESHOLDS.plus3) return 3;
  if (elapsed <= timer * CHEST_THRESHOLDS.plus2) return 2;
  if (elapsed <= timer) return 1;
  return 0;
}

type TrackerContent = Pick<ContentRegistry, 'dungeon' | 'encounter'>;
type TrackerWorld = DeepReadonly<Pick<World, 'mplus' | 'dungeonId' | 'encounter'>>;

/** Fill `out` from the world (allocation-free once `out.bosses` has the dungeon's length). */
export function fillTrackerView(world: TrackerWorld, content: TrackerContent, out: TrackerView): TrackerView {
  const m = world.mplus;
  const d = content.dungeon(world.dungeonId);
  out.dungeon = d?.name ?? world.dungeonId;
  out.level = m.level;
  out.affixes = m.affixes as readonly string[];
  out.phase = m.phase === 'completed' || m.phase === 'depleted' || m.phase === 'pre' ? m.phase : 'running';
  const timer = Math.max(1, m.timerMs);
  const elapsed = Math.max(0, m.elapsedMs);
  out.timerMs = timer;
  out.elapsedMs = elapsed;
  out.remainingMs = timer - elapsed;
  out.over = elapsed > timer;
  out.timeFrac = Math.min(1, elapsed / timer);
  out.plus3LeftMs = timer * CHEST_THRESHOLDS.plus3 - elapsed;
  out.plus2LeftMs = timer * CHEST_THRESHOLDS.plus2 - elapsed;
  out.chest = m.chest ?? chestFor(elapsed, timer);
  const req = Math.max(1, m.forcesRequired);
  out.forces = m.forces;
  out.forcesRequired = m.forcesRequired;
  out.forcesFrac = Math.min(1, Math.max(0, m.forces / req));
  // Floor (not round) so 99.96% never reads "100.0%" before the last mob dies.
  out.forcesTenths = Math.min(1000, Math.floor((m.forces / req) * 1000 + 1e-9));
  out.forcesDone = m.forces >= req;

  const ids = d?.requiredEncounters ?? [];
  const bosses = out.bosses;
  if (bosses.length !== ids.length) {
    bosses.length = 0;
    for (let i = 0; i < ids.length; i++) bosses.push({ id: '', name: '', killed: false, engaged: false });
  }
  const live = world.encounter && !world.encounter.result ? world.encounter.encounterId : null;
  let killed = 0;
  for (let i = 0; i < ids.length; i++) {
    const id = ids[i]!;
    const b = bosses[i]!;
    if (b.id !== id) {
      b.id = id;
      b.name = content.encounter(id)?.name ?? id;
    }
    b.killed = m.bossesKilled.includes(id);
    b.engaged = !b.killed && live === id;
    if (b.killed) killed++;
  }
  out.bossesKilled = killed;
  out.deaths = m.deaths;
  out.penaltyMs = m.deathPenaltyMs;
  out.wipes = m.wipes;
  out.inCombat = m.inCombat;
  return out;
}

/** "m:ss" (or "h:mm:ss") for a non-negative duration, floor seconds. */
export function clockText(ms: number): string {
  const t = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(t / 3600);
  const mm = Math.floor((t % 3600) / 60);
  const ss = t % 60;
  const s2 = ss < 10 ? `0${ss}` : `${ss}`;
  if (h > 0) return `${h}:${mm < 10 ? `0${mm}` : mm}:${s2}`;
  return `${mm}:${s2}`;
}

/** Affix display data (icons are inline SVG in tracker.ts). */
export const AFFIX_INFO: Readonly<Record<string, { name: string; text: string }>> = {
  fortified: { name: 'Fortified', text: 'Non-boss enemies have 20% more health and deal 30% more damage.' },
  tyrannical: { name: 'Tyrannical', text: 'Bosses have 30% more health and deal 15% more damage.' },
};

export function affixInfo(id: string): { name: string; text: string } {
  return AFFIX_INFO[id] ?? { name: id.replace(/[_-]+/g, ' ').replace(/^\w/, (c) => c.toUpperCase()), text: 'A keystone affix.' };
}
